"use client";
import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import InputField from "../../../components/common/InputField";
import RaidoField from "../../../components/common/RaidoField";
import SelectInput from "../../../components/Select";
import TextareaField from "../../../components/common/TextareaField";
import { useCreateTicket } from "../../../apis/ticketManagementClientApis";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { IconCloudUpload, IconX } from "@tabler/icons-react";

type Props = {
  onSuccess: () => void;
};

const CreateTicketModal = ({ onSuccess }: Props) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      subject: "",
      description: "",
      type: "BILLING",
      priority: "HIGH",
      awbNumber: "",
    }
  });

  const { mutate: createTicket, isPending } = useCreateTicket();

  const priorityMode = watch("priority");
  const typeValue = watch("type");
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<File[]>([]);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...filesArray]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: any) => {
    createTicket({ ...data, files: attachments }, {
      onSuccess: () => onSuccess()
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-[655px] p-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-3xl space-y-6">
      <div className="grid grid-cols-6 gap-5">
        <InputField
          className="col-span-6"
          label="Subject"
          type="text"
          placeholder="E.g., Payment failed during checkout"
          {...register("subject", { required: "Subject is required" })}
          error={errors.subject?.message as string}
        />
        
        <SelectInput
          className="col-span-3"
          label="Category"
          name="type"
          value={typeValue}
          setValue={(val) => setValue("type", val)}
          selectOptions={[
            { value: "GENERAL_INQUIRY", text: "General Inquiry" },
            { value: "BILLING", text: "Billing" },
            { value: "TECHNICAL", text: "Technical" },
            { value: "COMPLAINT", text: "Complaint" },
            { value: "FEATURE_REQUEST", text: "Feature Request" },
          ]}
        />
        
        <div className="col-span-3">
          <RaidoField
            label="Priority"
            value={priorityMode}
            onClick={(val) => {
              if (typeof val === "string") {
                setValue("priority", val.toUpperCase());
              }
            }}
            valuesArr={[
              { value: "LOW", text: "Low" },
              { value: "MEDIUM", text: "Medium" },
              { value: "HIGH", text: "High" },
              { value: "URGENT", text: "Urgent" },
            ]}
          />
        </div>

        <div className="col-span-6">
          <InputField
            label="AWB Number / Reference Number (Optional)"
            type="text"
            placeholder="E.g., GW123456789"
            {...register("awbNumber")}
            error={errors.awbNumber?.message as string}
          />
        </div>

        <div className="col-span-6">
          <TextareaField
            name="description"
            label="Description"
            placeholder="I tried to process a payment but it gave me a 500 internal server error..."
            register={register}
            validation={{ required: "Description is required" }}
            error={errors.description?.message as string}
          />
        </div>

        {/* Attachment Section */}
        <div className="col-span-6 space-y-2">
          <label className="text-[#1E293B] text-sm font-medium">Attachments</label>
          <div 
            onClick={handleFileClick}
            className="w-full border-2 border-dashed border-[#D2D2D2] rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-[#FBD037]/10 transition-colors bg-white"
          >
            <IconCloudUpload stroke={1.5} size={32} className="text-[#FBD037] mb-2" />
            <span className="text-sm text-[#212121] font-medium">Click to upload files</span>
            <span className="text-xs text-gray-400 mt-1">PNG, JPG, PDF up to 10MB</span>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              accept="image/png, image/jpeg, application/pdf"
              onChange={handleFileChange}
            />
          </div>

          {/* Preview Attached Files */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center gap-2 bg-white px-3 py-2 border border-[#D2D2D2] rounded-xl shadow-sm">
                  <span className="text-xs text-[#212121] max-w-[120px] truncate">{file.name}</span>
                  <button 
                    type="button" 
                    onClick={() => removeAttachment(index)}
                    className="text-red-500 hover:bg-red-50 p-1 rounded-full"
                  >
                    <IconX size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pt-4 border-t border-[#D2D2D2]/50 flex justify-end">
        <ButtonStyled
          value="Submit Ticket"
          isPending={isPending}
          type="submit"
        />
      </div>
    </form>
  );
};

export default CreateTicketModal;
